import { useState, useRef, useEffect, useMemo } from 'react';
import { Upload, Lock, ShieldAlert, AlertCircle, Ban, Trash2, CheckCircle2, FileText, FileType, Search, X, Filter } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SiteHeader } from '@/components/site-header';
import { EmptyState } from '@/components/ui/empty-state';
import { FileCardSkeleton } from './components/file-card-skeleton';
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { getAllFiles, createFile, deleteFile } from '@/api/files/files.api';
import type { FileDetails } from '@/api/files/files.types';
import { FileCard } from './components/file-card';
import { UploadProgress } from './components/upload-progress';
import { ALLOWED_FILE_TYPES, isAllowedFileType } from './utils';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface UploadState {
    status: 'uploading';
    currentFile: number;
    totalFiles: number;
    fileName: string;
}

// File type options with icons and colors
const FILE_TYPE_OPTIONS = [
    { value: 'all', label: 'All Files', icon: Filter, color: 'text-foreground' },
    { value: 'pdf', label: 'PDF', icon: FileType, color: 'text-red-500' },
    { value: 'doc', label: 'Word', icon: FileText, color: 'text-blue-500' },
    { value: 'txt', label: 'Text', icon: FileText, color: 'text-gray-500' },
];

const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

const Files = () => {
    const [files, setFiles] = useState<FileDetails[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [fileTypeFilter, setFileTypeFilter] = useState('all');
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadState, setUploadState] = useState<UploadState | null>(null);
    const dragCounter = useRef(0);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user } = useAuth();
    const canUploadFiles = user?.permissions?.files === 'EDITOR';
    const { error, success } = useEnhancedToast();

    // Filter files based on search query and file type
    const filteredFiles = useMemo(() => {
        let result = files;

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(file => 
                file.name.toLowerCase().includes(query) ||
                file.id.toLowerCase().includes(query)
            );
        }

        // Apply file type filter
        if (fileTypeFilter !== 'all') {
            result = result.filter(file => {
                const ext = getFileExtension(file.name);
                switch (fileTypeFilter) {
                    case 'pdf':
                        return ext === 'pdf';
                    case 'doc':
                        return ext === 'doc' || ext === 'docx';
                    case 'txt':
                        return ext === 'txt';
                    default:
                        return true;
                }
            });
        }

        return result;
    }, [files, searchQuery, fileTypeFilter]);

    useEffect(() => {
        fetchFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchFiles = async () => {
        try {
            const { data, error: fetchError } = await getAllFiles();
            if (fetchError) {
                error({
                    icon: AlertCircle,
                    title: "Failed to Load Files",
                    description: fetchError,
                    duration: 4000,
                });
                return;
            }
            if (data) {
                // Sort files by createdAt in ascending order (oldest/first uploaded first)
                const sortedFiles = [...data].sort((a, b) =>
                    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                );
                setFiles(sortedFiles);
            }
        } catch {
            error({
                icon: AlertCircle,
                title: "Failed to Load Files",
                description: "Could not fetch knowledge base files. Please try again.",
                duration: 4000,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDrag = (e: React.DragEvent<HTMLDivElement>, entering: boolean) => {
        e.preventDefault();
        e.stopPropagation();

        if (entering) {
            dragCounter.current += 1;
        } else {
            dragCounter.current -= 1;
        }

        if (dragCounter.current === 1) {
            setIsDragging(true);
        } else if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (selectedFiles) {
            processFiles(selectedFiles);
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles) {
            processFiles(droppedFiles);
        }
    };

    const processFiles = async (fileList: FileList) => {
        if (!canUploadFiles) {
            error({
                icon: Ban,
                title: "Permission Denied",
                description: "You need editor permissions to upload files",
                duration: 4000,
            });
            return;
        }

        const newFiles = Array.from(fileList).filter(file => {
            const isAllowed = isAllowedFileType(file);
            if (!isAllowed) {
                error({
                    icon: AlertCircle,
                    title: "Unsupported File Type",
                    description: "Please upload only PDF, Word (.doc, .docx), or plain text files.",
                    duration: 4000,
                });
            }
            return isAllowed;
        });

        if (newFiles.length > 0) {
            setIsUploading(true);
            setUploadState({
                status: 'uploading',
                currentFile: 0,
                totalFiles: newFiles.length,
                fileName: newFiles[0].name
            });

            try {
                for (let i = 0; i < newFiles.length; i++) {
                    const file = newFiles[i];
                    setUploadState(prev => ({
                        ...prev!,
                        currentFile: i + 1,
                        fileName: file.name
                    }));

                    const formData = new FormData();
                    formData.append('file', file);

                        const { data, error: uploadError } = await createFile({ file });
                        if (uploadError) {
                            error({
                                icon: AlertCircle,
                                title: "Upload Failed",
                                description: `Failed to upload ${file.name}: ${uploadError}`,
                                duration: 4000,
                            });
                            setUploadState(null);
                            break;
                        } else if (data) {
                            // Add new file and re-sort to maintain order (oldest first)
                            setFiles(prev => [...prev, data].sort((a, b) =>
                                new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                            ));
                        }
                    }

                    // Hide upload progress
                    setUploadState(null);
                    
                    // Show success toast
                    success({
                        icon: CheckCircle2,
                        title: "Upload Complete",
                        description: `Successfully uploaded ${newFiles.length} ${newFiles.length === 1 ? 'file' : 'files'}`,
                        duration: 3000,
                    });

                } catch {
                    error({
                        icon: AlertCircle,
                        title: "Upload Failed",
                        description: "An error occurred while uploading files.",
                        duration: 4000,
                    });
                } finally {
                    setIsUploading(false);
                    setUploadState(null);
                }
            }
        };

    const handleDelete = async (fileId: string, fileName: string) => {
        try {
            const { error: deleteError } = await deleteFile(fileId);
            if (deleteError) {
                error({
                    icon: Trash2,
                    title: "Delete Failed",
                    description: `Failed to delete ${fileName}: ${deleteError}`,
                    duration: 4000,
                });
            } else {
                setFiles(prev => prev.filter(file => file.id !== fileId));
                success({
                    icon: CheckCircle2,
                    title: "File Deleted",
                    description: `Successfully deleted ${fileName}`,
                    duration: 3000,
                });
            }
        } catch {
            error({
                icon: AlertCircle,
                title: "Delete Failed",
                description: "An error occurred while deleting the file.",
                duration: 4000,
            });
        }
    };

    return (
        <div className="flex flex-col h-full">
            <SiteHeader
                title="Knowledge Base Files"
                subtitle="Upload and manage files for your knowledge base"
            >
                {files.length > 0 && (
                    <>
                        {/* Search Input */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                            <Input
                                placeholder="Search by name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-[200px] lg:w-[300px] pl-9 pr-9"
                            />
                            {searchQuery && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>

                        {/* File Type Filter */}
                        <Combobox
                            options={FILE_TYPE_OPTIONS.map(type => ({
                                label: type.label,
                                value: type.value,
                                icon: type.icon,
                                iconClassName: type.color,
                            }))}
                            value={fileTypeFilter}
                            onValueChange={setFileTypeFilter}
                            placeholder="All Files"
                            searchPlaceholder="Search file types..."
                            emptyMessage="No file type found"
                            className="w-[170px]"
                        />

                        {/* Upload Button */}
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div>
                                        <Button
                                            onClick={() => canUploadFiles && fileInputRef.current?.click()}
                                            size="sm"
                                            className="gap-2"
                                            disabled={isUploading || !canUploadFiles}
                                        >
                                            <Upload className="h-4 w-4" />
                                            {isUploading ? 'Uploading...' : 'Upload Files'}
                                            {!canUploadFiles && <Lock className="h-3.5 w-3.5 opacity-70" />}
                                        </Button>
                                    </div>
                                </TooltipTrigger>
                                {!canUploadFiles && (
                                    <TooltipContent className="flex items-center gap-2 max-w-xs">
                                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                                        <p>You need editor permissions to upload files. Contact your administrator for access.</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    multiple
                    accept={Object.entries(ALLOWED_FILE_TYPES)
                        .map(([mime, ext]) => `${mime},${ext}`)
                        .join(',')}
                />
            </SiteHeader>

            <div 
                className="flex-1 overflow-auto"
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragEnter={(e) => handleDrag(e, true)}
                onDragLeave={(e) => handleDrag(e, false)}
                onDrop={handleDrop}
            >
                {isLoading ? (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="px-4 py-6 md:px-8 md:py-8"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, index) => (
                                <FileCardSkeleton key={index} />
                            ))}
                        </div>
                    </motion.div>
                    ) : files.length > 0 ? (
                        <>
                            {isDragging && (
                                <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
                                    <div className="h-full p-8">
                                        <div className="h-full border-2 border-dashed rounded-lg border-primary bg-primary/5 flex items-center justify-center">
                                            <div className="text-center space-y-4 px-4 max-w-md mx-auto">
                                                <div className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center bg-primary/20">
                                                    <Upload className="h-8 w-8 text-primary" />
                                                </div>
                                                <div className="space-y-2">
                                                    <h3 className="font-semibold text-lg text-foreground">
                                                        {canUploadFiles ? 'Drop to upload files' : 'Upload permission required'}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground">
                                                        {canUploadFiles 
                                                            ? 'Release to upload your files'
                                                            : 'You need editor permissions to upload files'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {filteredFiles.length === 0 ? (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-center justify-center"
                                    style={{ height: 'calc(100vh - 200px)' }}
                                >
                                    <div className="text-center">
                                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                            <Search className="h-8 w-8 text-muted-foreground" />
                                        </div>
                                        <h3 className="text-lg font-semibold mb-2">No Matching Files</h3>
                                        <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                                            We couldn't find any files matching your {searchQuery && fileTypeFilter !== 'all' ? 'search and filter' : searchQuery ? 'search query' : 'filter'}.
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setSearchQuery('');
                                                setFileTypeFilter('all');
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className={cn(
                                        "px-4 py-6 md:px-8 md:py-8",
                                        isDragging && "opacity-30"
                                    )}
                                >
                                    <motion.div
                                        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                                    >
                                        {filteredFiles.map((file) => (
                                            <FileCard
                                                key={file.id}
                                                file={file}
                                                onDelete={() => handleDelete(file.id, file.name)}
                                            />
                                        ))}
                                    </motion.div>
                                </motion.div>
                            )}
                        </>
                ) : (
                    <>
                        {isDragging && (
                            <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
                                <div className="h-full p-8">
                                    <div className="h-full border-2 border-dashed rounded-lg border-primary bg-primary/5 flex items-center justify-center">
                                        <div className="text-center space-y-4 px-4 max-w-md mx-auto">
                                            <div className="mx-auto w-16 h-16 rounded-xl flex items-center justify-center bg-primary/20">
                                                <Upload className="h-8 w-8 text-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <h3 className="font-semibold text-lg text-foreground">
                                                    {canUploadFiles ? 'Drop to upload files' : 'Upload permission required'}
                                                </h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {canUploadFiles 
                                                        ? 'Release to upload your files'
                                                        : 'You need editor permissions to upload files'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="h-full flex items-center justify-center">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                className={cn(
                                    "w-full -mt-12",
                                    isDragging && "opacity-30"
                                )}
                            >
                                <EmptyState
                                    icon={FileText}
                                    title="No Files Yet"
                                    description="Upload files to your knowledge base. Your assistants can access these files during conversations."
                                    secondaryDescription="Drag & drop files here or click the button below to browse."
                                    action={{
                                        label: "Upload Files",
                                        onClick: () => fileInputRef.current?.click(),
                                        icon: Upload,
                                        disabled: !canUploadFiles,
                                        disabledReason: "You need editor permissions to upload files. Contact your administrator for access.",
                                    }}
                                    infoCard={{
                                        content: (
                                            <div className="space-y-3">
                                                <p className="text-xs font-medium text-muted-foreground text-center">Supported File Formats</p>
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <FileType className="h-4 w-4 text-red-500 shrink-0" />
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs font-medium">PDF</p>
                                                            <p className="text-xs text-muted-foreground">.pdf</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs font-medium">Word</p>
                                                            <p className="text-xs text-muted-foreground">.doc, .docx</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs font-medium">Text</p>
                                                            <p className="text-xs text-muted-foreground">.txt</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }}
                                />
                            </motion.div>
                        </div>
                    </>
                )}
            </div>
            <AnimatePresence mode="sync">
                {uploadState && (
                    <UploadProgress
                        key="upload-progress"
                        {...uploadState}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default Files;
